import { useEffect, useState, DependencyList } from 'react';
import { WidgetModel } from '@jupyter-widgets/base';

import { useWidgetModelContext } from './widget-model-context';
import { EventHandler } from 'backbone';

interface ModelCallback {
  (model: WidgetModel, event: EventHandler): void;
}

export function useModelState<T, K extends keyof T = keyof T>(
  name: K
): [T[K], (val: T[K], options?: any) => void] {
  const model = useWidgetModelContext();
  const stringName = String(name);
  const [state, setState] = useState<T[K]>(() => model.get(stringName));

  useModelEvent(
    `change:${stringName}`,
    model => {
      setState(model.get(stringName));
    },
    [name]
  );

  function updateModel(val: T[K], options?: any) {
    model.set(name, val, options);
    model.save_changes();
  }

  return [state, updateModel];
}

export function useModelEvent(
  event: string,
  callback: ModelCallback,
  deps?: DependencyList | undefined
) {
  const model = useWidgetModelContext();
  const dependencies = deps === undefined ? [model] : [...deps, model];

  useEffect(() => {
    const callbackWrapper = (e: Backbone.EventHandler) => callback(model, e);
    model.on(event, callbackWrapper);
    return () => void model.unbind(event, callbackWrapper);
  }, dependencies);
}
